/**
 * Dashboard de um formulario — /comercial/formularios/:id/dashboard.
 *
 * Metricas agregadas das submissoes (consome obterMetricasFormulario):
 *   - KpiStrip: Total respostas / Leads criados ou enriquecidos (rotulo por
 *     tipo, sem "Conversao" — FORM-24) / Envios recusados 24h (FORM-13) /
 *     Publicas / Internas
 *   - Envios recusados (validacao, chave desconhecida, anti-spam...) — para
 *     "ninguem preencheu" e "todo envio esta sendo recusado" nao parecerem
 *     iguais (FORM-13). Aparece mesmo sem resposta.
 *   - Respostas por dia (30d) — area chart (recharts)
 *   - Origem das respostas — donut (recharts)
 *   - Distribuicao de cada campo de escolha (SELECT/RADIO/CHECKBOX/MULTI) —
 *     barras horizontais por opcao
 *   - Preenchimento por campo — "N de M em que o campo apareceu" (FORM-25)
 *   - Campos removidos que ainda tem resposta guardada (FORM-17)
 *
 * Layout em cards (DashGrid) + tokens canonicos (var(--chart-N)). Estados de
 * loading / erro / vazio. Sem scroll lateral (barras + ellipsis).
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FiAlertTriangle, FiBarChart2, FiInbox } from "react-icons/fi";
import {
  Area,
  AreaChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import EntityHeader from "../../components/EntityHeader";
import KpiStrip from "../../components/KpiStrip";
import { useAuth } from "../../context/AuthContext";
import {
  TIPO_CAMPO_LABEL,
  obterFormulario,
  obterMetricasFormulario,
} from "../../services/formularios";
import { formatarPercentual } from "../../utils/dinheiro";
import { PageContainer, PageState } from "../../styles/shared";

import FormTabs from "./FormTabs";
import { formatarRecebidaEm, rotuloMotivoRecusa, rotuloOrigem } from "./respostasFormat";
import { DashCard, DashGrid, DistRow, FieldFillRow } from "./styles";

const CHART_CORES = [
  "var(--chart-1)",
  "var(--chart-2)",
  "var(--chart-3)",
  "var(--chart-4)",
  "var(--chart-5)",
  "var(--chart-6)",
  "var(--chart-7)",
  "var(--chart-8)",
];

// Tom canonico por origem (cor so comunica estado).
const ORIGEM_COR = {
  PUBLICO: "var(--chart-3)", // sky/info
  INTERNO: "var(--chart-4)", // amber/neutral-ish
};
// "2026-06-03" -> "03/06" para o eixo X da serie diaria.
function diaCurto(iso) {
  if (!iso) return "";
  const [, m, d] = iso.split("-");
  return `${d}/${m}`;
}

// Tooltip enxuto compartilhado (1 serie: valor inteiro).
function ChartTooltip({ active, payload, label }) {
  if (!active || !payload || payload.length === 0) return null;
  const v = payload[0].value;
  return (
    <div
      style={{
        background: "var(--surface)",
        border: "1px solid var(--border)",
        borderRadius: "var(--radius-sm)",
        padding: "6px 10px",
        fontSize: 12,
        color: "var(--ink)",
        boxShadow: "var(--shadow-1)",
      }}
    >
      {label != null && (
        <div style={{ color: "var(--ink-3)", marginBottom: 2 }}>{label}</div>
      )}
      <strong>{v}</strong> resposta(s)
    </div>
  );
}

// --- Card: distribuicao de um campo de escolha (barras horizontais) ---------
function DistribuicaoCampo({ campo }) {
  const dist = campo.distribuicao || [];
  const max = Math.max(1, ...dist.map((o) => o.count));
  const totalMarcado = dist.reduce((s, o) => s + o.count, 0);
  return (
    <DashCard>
      <h3 title={campo.label}>{campo.label}</h3>
      <p className="dash-card__sub">
        {TIPO_CAMPO_LABEL[campo.tipo] || campo.tipo} · {campo.respondido} de{" "}
        {campo.exibido} em que apareceu ({formatarPercentual(campo.taxa)})
      </p>
      {dist.length === 0 || totalMarcado === 0 ? (
        <p className="tiny muted">Nenhuma opção marcada ainda.</p>
      ) : (
        <div>
          {dist.map((opc, idx) => {
            const pct = (opc.count / max) * 100;
            return (
              <DistRow key={`${opc.valor}-${idx}`}>
                <span className="dist-label" title={opc.valor}>
                  {opc.valor}
                </span>
                <span className="dist-track">
                  <span
                    style={{
                      width: `${Math.max(pct, opc.count > 0 ? 4 : 0)}%`,
                      background: CHART_CORES[idx % CHART_CORES.length],
                    }}
                  />
                </span>
                <span className="dist-count">{opc.count}</span>
              </DistRow>
            );
          })}
        </div>
      )}
    </DashCard>
  );
}

export default function Dashboard() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { request } = useAuth();

  const [form, setForm] = useState(null);
  const [metricas, setMetricas] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null);

  const carregar = useCallback(async () => {
    setLoading(true);
    setErro(null);
    try {
      const [detalhe, m] = await Promise.all([
        obterFormulario(request, id),
        obterMetricasFormulario(request, id),
      ]);
      setForm(detalhe);
      setMetricas(m);
    } catch (err) {
      setErro(err?.message || "Erro ao carregar metricas.");
    } finally {
      setLoading(false);
    }
  }, [request, id]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const kpis = useMemo(() => {
    if (!metricas) return [];
    const origem = Object.fromEntries(
      (metricas.por_origem || []).map((o) => [o.origem, o.count])
    );
    const itens = [{ label: "Total de respostas", value: metricas.total_respostas }];
    // Rotulo por tipo ("Leads criados" / "Leads enriquecidos"), nunca
    // "Conversao": nao ha contagem de visualizacoes, logo nao ha denominador.
    // GENERICO nao tem KPI de lead. Teste e FALHOU ficam fora (FORM-24).
    if (metricas.rotulo_leads) {
      const enriquecer = metricas.rotulo_leads === "Leads enriquecidos";
      itens.push({
        label: metricas.rotulo_leads,
        value: enriquecer ? metricas.leads_enriquecidos : metricas.leads_criados,
        sub: `${formatarPercentual(metricas.percentual_leads)} de ${metricas.respostas_consideradas} consideradas`,
        title: "Fora da conta: respostas de teste e as que falharam no CRM.",
      });
    }
    const recusas = metricas.recusas || {};
    itens.push({
      label: "Envios recusados (24h)",
      value: recusas.ultimas_24h ?? 0,
      sub: recusas.ultimo_motivo ? `último: ${rotuloMotivoRecusa(recusas.ultimo_motivo)}` : undefined,
      tone: recusas.ultimas_24h > 0 ? "warning" : undefined,
    });
    itens.push({ label: "Públicas", value: origem.PUBLICO || 0 });
    itens.push({ label: "Internas", value: origem.INTERNO || 0 });
    return itens;
  }, [metricas]);

  // Serie diaria pronta para o AreaChart.
  const serieDia = useMemo(
    () =>
      (metricas?.por_dia || []).map((d) => ({
        dia: diaCurto(d.data),
        count: d.count,
      })),
    [metricas]
  );

  // Origem pronta para o donut (so origens com ao menos 1).
  const origemData = useMemo(
    () =>
      (metricas?.por_origem || [])
        .filter((o) => o.count > 0)
        .map((o) => ({
          name: rotuloOrigem(o.origem),
          value: o.count,
          fill: ORIGEM_COR[o.origem] || "var(--chart-6)",
        })),
    [metricas]
  );

  // Campos de escolha (com distribuicao) e campos com input (preenchimento).
  const camposEscolha = useMemo(
    () => (metricas?.por_campo || []).filter((c) => Array.isArray(c.distribuicao)),
    [metricas]
  );
  const camposPreenchimento = useMemo(
    () => (metricas?.por_campo || []).filter((c) => c.tipo !== "SECAO"),
    [metricas]
  );

  if (loading) {
    return (
      <PageContainer>
        <PageState>Carregando metricas...</PageState>
      </PageContainer>
    );
  }

  if (erro) {
    return (
      <PageContainer>
        <EntityHeader
          onBack={() => navigate(`/comercial/formularios/${id}`)}
          backLabel="Editor"
          title="Dashboard"
        />
        <FormTabs id={id} ativa="dashboard" />
        <PageState $tone="error">{erro}</PageState>
      </PageContainer>
    );
  }

  const semRespostas = !metricas || metricas.total_respostas === 0;
  const recusas = metricas?.recusas || {};
  const temRecusas = (recusas.total_30d || 0) > 0;
  const camposRemovidos = metricas?.campos_removidos || [];

  return (
    <PageContainer>
      <EntityHeader
        onBack={() => navigate(`/comercial/formularios/${id}`)}
        backLabel="Editor"
        title={form ? `Dashboard — ${form.nome}` : "Dashboard"}
        subtitle={
          metricas?.ultima_em
            ? `Última resposta em ${formatarRecebidaEm(metricas.ultima_em)}`
            : form?.slug
              ? `/f/${form.slug}`
              : undefined
        }
      />

      <FormTabs id={id} ativa="dashboard" />

      <KpiStrip items={kpis} />

      {/* Recusas aparecem MESMO sem resposta: e' o caso que importa (FORM-13). */}
      {temRecusas && (
        <DashCard style={{ marginBottom: 16 }} aria-label="Envios recusados">
          <h3 style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <FiAlertTriangle size={15} aria-hidden="true" /> Envios recusados
          </h3>
          <p className="dash-card__sub">
            Hoje {recusas.hoje ?? 0} · últimas 24 h {recusas.ultimas_24h ?? 0} · 30 dias{" "}
            {recusas.total_30d ?? 0}. Contados sem dado pessoal: só o motivo.
          </p>
          <div>
            {(recusas.por_motivo || []).map((m, idx) => (
              <DistRow key={m.motivo}>
                <span className="dist-label" title={rotuloMotivoRecusa(m.motivo)}>
                  {rotuloMotivoRecusa(m.motivo)}
                </span>
                <span className="dist-track">
                  <span
                    style={{
                      width: `${Math.max((m.total / Math.max(1, recusas.total_30d || 1)) * 100, 4)}%`,
                      background: CHART_CORES[idx % CHART_CORES.length],
                    }}
                  />
                </span>
                <span className="dist-count">{m.total}</span>
              </DistRow>
            ))}
          </div>
          {recusas.ultimo_detalhe && (
            <p className="tiny muted" style={{ marginTop: 8 }}>
              Última recusa em {formatarRecebidaEm(recusas.ultima_em)}: {recusas.ultimo_detalhe}
            </p>
          )}
        </DashCard>
      )}

      {semRespostas ? (
        <div className="empty">
          <div className="art">
            <FiInbox size={40} aria-hidden="true" />
          </div>
          <p className="title">Sem respostas para analisar</p>
          <p className="msg">
            Os graficos aparecem aqui assim que o formulario receber as
            primeiras submissoes.
          </p>
        </div>
      ) : (
        <>
          {/* Serie diaria + origem lado a lado */}
          <DashGrid>
            <DashCard style={{ gridColumn: "1 / -1" }}>
              <h3>Respostas por dia</h3>
              <p className="dash-card__sub">Últimos 30 dias</p>
              <ResponsiveContainer width="100%" height={220}>
                <AreaChart
                  data={serieDia}
                  margin={{ top: 6, right: 8, left: -16, bottom: 0 }}
                >
                  <defs>
                    <linearGradient id="fbDiaFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="var(--chart-1)" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="var(--chart-1)" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--divider)" />
                  <XAxis
                    dataKey="dia"
                    tick={{ fontSize: 10, fill: "var(--ink-3)" }}
                    interval="preserveStartEnd"
                    minTickGap={18}
                  />
                  <YAxis
                    allowDecimals={false}
                    tick={{ fontSize: 10, fill: "var(--ink-3)" }}
                    width={32}
                  />
                  <Tooltip content={<ChartTooltip />} />
                  <Area
                    type="monotone"
                    dataKey="count"
                    stroke="var(--chart-1)"
                    strokeWidth={2}
                    fill="url(#fbDiaFill)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </DashCard>

            <DashCard>
              <h3>Origem das respostas</h3>
              <p className="dash-card__sub">Link público vs painel interno</p>
              {origemData.length === 0 ? (
                <p className="tiny muted">Sem dados.</p>
              ) : (
                <>
                  <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                      <Pie
                        data={origemData}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        innerRadius={48}
                        outerRadius={78}
                        paddingAngle={2}
                      >
                        {origemData.map((entry) => (
                          <Cell key={entry.name} fill={entry.fill} />
                        ))}
                      </Pie>
                      <Tooltip content={<ChartTooltip />} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div style={{ fontSize: 12, marginTop: 4 }}>
                    {origemData.map((d) => (
                      <div
                        key={d.name}
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          padding: "2px 0",
                        }}
                      >
                        <span>
                          <span
                            style={{
                              display: "inline-block",
                              width: 10,
                              height: 10,
                              background: d.fill,
                              marginRight: 6,
                              borderRadius: 2,
                            }}
                          />
                          {d.name}
                        </span>
                        <strong>{d.value}</strong>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </DashCard>

            {/* Preenchimento por campo */}
            <DashCard>
              <h3>Preenchimento por campo</h3>
              <p className="dash-card__sub">
                Quantas respostas preencheram cada campo, entre as que o viram
                (campo oculto pela condição ou criado depois não conta)
              </p>
              {camposPreenchimento.length === 0 ? (
                <p className="tiny muted">Este formulario não tem campos.</p>
              ) : (
                <div>
                  {camposPreenchimento.map((c) => (
                    <FieldFillRow key={c.campo_id}>
                      <div className="fill-name">
                        <span className="nm" title={c.label}>
                          {c.label}
                        </span>
                      </div>
                      <span className="fill-track">
                        <span style={{ width: `${c.taxa}%` }} />
                      </span>
                      <span className="fill-val" title={`${c.respondido} de ${c.exibido} em que o campo apareceu`}>
                        {c.respondido} de {c.exibido} · {formatarPercentual(c.taxa)}
                      </span>
                    </FieldFillRow>
                  ))}
                </div>
              )}
            </DashCard>
          </DashGrid>

          {/* Campos que sairam do formulario, mas ainda tem resposta (FORM-17) */}
          {camposRemovidos.length > 0 && (
            <DashCard style={{ marginTop: 16 }} aria-label="Campos removidos">
              <h3>Campos removidos</h3>
              <p className="dash-card__sub">
                Saíram do formulário; as respostas continuam em Respostas e na exportação.
              </p>
              <div>
                {camposRemovidos.map((c) => (
                  <FieldFillRow key={c.campo_id}>
                    <div className="fill-name">
                      <span className="nm" title={c.label}>
                        {c.label}
                      </span>
                    </div>
                    <span className="fill-track" aria-hidden="true" />
                    <span className="fill-val">{c.total_respondido} resposta(s)</span>
                  </FieldFillRow>
                ))}
              </div>
            </DashCard>
          )}

          {/* Distribuicao por campo de escolha */}
          {camposEscolha.length > 0 && (
            <>
              <h2
                style={{
                  fontFamily: "var(--font-display)",
                  fontSize: 15,
                  fontWeight: 600,
                  color: "var(--ink)",
                  margin: "20px 0 12px",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <FiBarChart2 size={16} aria-hidden="true" /> Distribuicao das
                escolhas
              </h2>
              <DashGrid>
                {camposEscolha.map((c) => (
                  <DistribuicaoCampo key={c.campo_id} campo={c} />
                ))}
              </DashGrid>
            </>
          )}
        </>
      )}
    </PageContainer>
  );
}
